import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import CommentsSection from './CommentsSection';
import { ReactionButton } from './ReactionButton';
import { OnlineDot } from './OnlineIndicator';
import { RenderMentionText } from './MentionInput';
import FeedPollCard from './FeedPollCard';
import ProductLinkCard from './ProductLinkCard';
import { parseUtc } from '../lib/calendarDate';
import {
  MessageCircle, Share2, Video, Users, CalendarDays, ExternalLink,
  MoreHorizontal, Pencil, Trash2, ChevronRight, Music,
  FileText, Link as LinkIcon, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Brief LOT2.1 (RCA) — Feed.js:FeedPostCard et ProfileWall.js:WallPost avaient
   divergé sur ~10 sprints : le mur de profil n'interprétait pas les shortcodes
   [product:]/[community:], ne gérait ni audio/document/lien OG, pas de
   lightbox, formats de date différents. Ce composant UNIQUE est désormais la
   seule source de rendu d'une publication (Feed + Profil). */

export const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  if (url.includes('preview.emergentagent.com/api/uploads')) {
    const match = url.match(/\/api\/uploads\/.+/);
    if (match) return `${BACKEND_URL}${match[0]}`;
  }
  if (url.startsWith('http://0.0.0.0') || url.startsWith('http://localhost')) {
    try { return `${BACKEND_URL}${new URL(url).pathname}`; } catch { return url; }
  }
  return url;
};

const getMediaType = (url) => {
  if (!url) return 'link';
  const lower = url.toLowerCase();
  if (lower.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)($|\?)/)) return 'image';
  if (lower.match(/\.(mp4|webm|mov|avi|mkv)($|\?)/)) return 'video';
  if (lower.match(/\.(mp3|wav|ogg|aac|flac|m4a)($|\?)/)) return 'audio';
  if (lower.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv)($|\?)/)) return 'document';
  return 'link';
};

// Brief LOT4.2 — détection lien YouTube pour aperçu embarqué (player intégré)
export const getYouTubeId = (url) => {
  const m = String(url || '').match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const parseRichContent = (content) => {
  if (!content) return { text: '', products: [], communities: [] };
  const products = [];
  const communities = [];
  let text = content;
  const productRegex = /\[product:([^:]+):([^\]]+)\]/g;
  let match;
  while ((match = productRegex.exec(content)) !== null) products.push({ id: match[1], title: match[2] });
  text = text.replace(productRegex, '').trim();
  const communityRegex = /\[community:([^:]+):([^\]]+)\]/g;
  while ((match = communityRegex.exec(content)) !== null) communities.push({ id: match[1], title: match[2] });
  text = text.replace(communityRegex, '').trim();
  text = text.replace(/\n\n📦\s*.+/g, '').replace(/\n\n👥\s*.+/g, '').trim();
  return { text, products, communities };
};

const formatRelativeDate = (d) => {
  const date = parseUtc(d) || new Date(d);
  const now = new Date();
  const diff = Math.floor((now - date) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `Il y a ${diff} min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const ImageLightbox = ({ src, onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="image-lightbox">
    <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors z-10">
      <X className="h-6 w-6" />
    </button>
    <img src={src} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
  </div>
);

/* ---------- Event card (partagé Feed + Wall) ---------- */
export const EventFeedCard = ({ event, navigate, currentUser, bookedEventIds, testIdPrefix = '' }) => {
  if (!event) return null;
  const start = parseUtc(event.start_time);
  const end = parseUtc(event.end_time);
  const dateStr = start ? start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
  const timeStr = start && end
    ? `${start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
    : '';
  const visioLink = event.visio_link || event.meeting_link || '';
  const ownerId = event.user_id || event.owner_id || '';
  const eventId = event.event_id || '';
  const isBookable = event.type === 'public_event' || event.type === 'public_slot' || event.type === 'public_series';
  const isPast = end ? end.getTime() < Date.now() : false;
  const isOwner = !!(currentUser?.user_id && ownerId && currentUser.user_id === ownerId);
  const isBooked = !!(eventId && bookedEventIds && bookedEventIds.has(eventId));
  const canJoin = !isPast && !!visioLink && (isOwner || isBooked);
  const canBook = !isPast && !isOwner && !isBooked && isBookable;

  const addToCalendar = () => {
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
    const esc = (s = '') => String(s).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
    const uid = `${eventId || Math.random().toString(36).slice(2)}@opensapiens.ch`;
    const descRaw = visioLink
      ? (event.description ? `Lien visio: ${visioLink}\n\n${event.description}` : `Lien visio: ${visioLink}`)
      : (event.description || '');
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//OpenSapiens//FR', 'METHOD:PUBLISH',
      'BEGIN:VEVENT', `UID:${uid}`, `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
      `SUMMARY:${esc(event.title)}`, `DESCRIPTION:${esc(descRaw)}`, `LOCATION:${visioLink}`,
    ];
    if (visioLink) { lines.push(`URL:${visioLink}`); lines.push(`X-MICROSOFT-SKYPETEAMSMEETINGURL:${visioLink}`); }
    lines.push('STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR');
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(event.title || 'event').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800/40 rounded-xl p-4" data-testid={testIdPrefix ? `${testIdPrefix}event-share` : 'event-feed-card'}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-14 bg-white dark:bg-gray-800 rounded-lg shadow-sm flex flex-col items-center justify-center border border-purple-100 dark:border-purple-800">
          <span className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase">{start ? start.toLocaleDateString('fr-FR', { month: 'short' }) : '—'}</span>
          <span className="text-lg font-bold text-gray-900 dark:text-white leading-none">{start ? start.getDate() : '—'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{event.title}</h4>
          {dateStr && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 capitalize">{dateStr} &middot; {timeStr}</p>}
          {event.description && <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{event.description}</p>}
          <div className="flex items-center flex-wrap gap-2 mt-3">
            {canJoin && (
              <a href={visioLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-colors" data-testid="event-join-btn">
                <Video className="h-3.5 w-3.5" />Rejoindre la session
              </a>
            )}
            {canBook && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/event/${eventId}?book=1`); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="event-book-btn"
              >
                <Users className="h-3.5 w-3.5" />Réserver
              </button>
            )}
            {eventId && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/event/${eventId}`); }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 px-3 py-1.5 rounded-lg transition-colors"
                data-testid="event-view-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" />Voir l&apos;événement
              </button>
            )}
            {!isPast && (isOwner || isBooked) && (
              <button onClick={addToCalendar} className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 px-3 py-1.5 rounded-lg transition-colors" data-testid="event-add-calendar-btn">
                <CalendarDays className="h-3.5 w-3.5" />Ajouter au calendrier
              </button>
            )}
            {!isPast && !isOwner && !isBooked && !event.is_free && event.price > 0 && (
              <span className="text-xs font-bold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 px-2.5 py-1 rounded-full">{event.price} {event.currency || 'CHF'}</span>
            )}
            {!isPast && !isOwner && !isBooked && event.is_free && (
              <span className="text-xs font-bold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 px-2.5 py-1 rounded-full">Gratuit</span>
            )}
            {isPast && (
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-full" data-testid="event-past-badge">Terminé</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- PostCard — composant UNIQUE Feed + Profil (Brief LOT2.1) ---------- */
const PostCard = ({
  post,
  currentUser,
  bookedEventIds,
  canManage = false,
  onDelete,
  onShare,
  onEdit,
  expandedComments,
  setExpandedComments,
  variant = 'feed', // 'feed' | 'wall'
  highlighted = false,
}) => {
  const navigate = useNavigate();
  const { text, products, communities } = parseRichContent(post.content);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  const isWall = variant === 'wall';
  const pid = post.post_id;
  const commentsOpen = !!expandedComments[pid];
  const toggleComments = () => setExpandedComments((prev) => ({ ...prev, [pid]: !prev[pid] }));

  return (
    <Card
      id={`post-${pid}`}
      className={`hover:shadow-md transition-shadow dark:bg-gray-800 dark:border-gray-700 ${
        highlighted ? 'ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-900' : ''
      }`}
      data-testid={isWall ? `wall-post-${pid}` : 'feed-post'}
    >
      <CardContent className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-11 w-11 cursor-pointer" onClick={() => navigate(`/app/profile/${post.user_id}`)}>
                <AvatarImage src={resolveMediaUrl(post.author_avatar)} />
                <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">
                  {post.author_name?.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <OnlineDot userId={post.user_id} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm cursor-pointer hover:underline" onClick={() => navigate(`/app/profile/${post.user_id}`)} data-testid="post-author">
                {post.author_name}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatRelativeDate(post.created_at)}
                {post.edited && (
                  <span className="ml-1 text-gray-400 dark:text-gray-500" data-testid={isWall ? `wall-post-edited-${pid}` : 'post-edited-marker'}>· modifié</span>
                )}
              </p>
            </div>
          </div>
          {canManage && (
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
                title="Options"
                aria-label="Options"
                data-testid={isWall ? `wall-post-menu-${pid}` : 'post-menu-trigger'}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-100 dark:border-gray-600 py-1 z-20 min-w-[160px]" data-testid="post-menu-dropdown">
                  <button
                    onClick={() => { setMenuOpen(false); onEdit && onEdit(post); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    data-testid={isWall ? `wall-post-edit-${pid}` : 'post-menu-edit'}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Modifier
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onDelete && onDelete(pid); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    data-testid={isWall ? `wall-post-delete-${pid}` : 'post-menu-delete'}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content — pleine largeur (Brief LOT2.3) */}
        {text && <RenderMentionText text={text} navigate={navigate} className="text-gray-800 dark:text-gray-200 whitespace-pre-line text-sm mb-3 block" />}

        {/* Poll */}
        {post.post_type === 'poll' && post.poll && (
          <div className="mb-3">
            <FeedPollCard postId={pid} poll={post.poll} onVote={() => {}} />
          </div>
        )}

        {/* Event share */}
        {post.post_type === 'event_share' && post.event_data && (
          <div className="mb-3">
            <EventFeedCard
              event={post.event_data}
              navigate={navigate}
              currentUser={currentUser}
              bookedEventIds={bookedEventIds}
              testIdPrefix={isWall ? `wall-${pid}-` : ''}
            />
          </div>
        )}

        {/* Rich Product Cards — Brief 27 oct B.4 : composant unique qui résout le vendeur */}
        {products.map((p, i) => (
          <ProductLinkCard key={`prod-${i}`} productId={p.id} title={p.title} hintSellerId={post.user_id} />
        ))}

        {/* Rich Community Cards */}
        {communities.map((c, i) => (
          <div
            key={`comm-${i}`}
            onClick={() => navigate(`/app/community/${c.id}`)}
            className="flex items-center gap-3 p-3 mb-3 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800/40 cursor-pointer hover:shadow-md transition-all group"
            data-testid="post-community-link"
          >
            <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/40 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="h-5 w-5 text-cyan-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-cyan-900 dark:text-cyan-200">{c.title}</p>
              <p className="text-xs text-cyan-500">Voir la communauté</p>
            </div>
            <ChevronRight className="h-5 w-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
          </div>
        ))}

        {/* Media */}
        {post.media_url && (() => {
          const ytId = getYouTubeId(post.media_url);
          if (ytId) {
            // Brief LOT4.2 — aperçu YouTube embarqué (player intégré 16:9)
            return (
              <div className="mb-3 rounded-xl overflow-hidden bg-black" data-testid="post-media-youtube">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${ytId}`}
                    title="YouTube"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            );
          }
          const mediaType = getMediaType(post.media_url);
          const resolvedUrl = resolveMediaUrl(post.media_url);
          return (
            <div className="mb-3 rounded-xl overflow-hidden" data-testid={isWall ? `wall-post-media-${pid}` : 'post-media'}>
              {mediaType === 'image' ? (
                <img
                  src={resolvedUrl}
                  alt="Publication"
                  className="w-full max-h-[400px] object-contain bg-gray-50 dark:bg-gray-900 rounded-xl cursor-pointer hover:opacity-95 transition-opacity"
                  onClick={() => setLightbox(resolvedUrl)}
                  data-testid={isWall ? `wall-post-image-${pid}` : 'post-media-image'}
                />
              ) : mediaType === 'video' ? (
                <video src={resolvedUrl} controls className="w-full max-h-[400px] rounded-xl bg-black" data-testid="post-media-video">
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              ) : mediaType === 'audio' ? (
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl flex items-center gap-3" data-testid="post-media-audio">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"><Music className="h-6 w-6 text-purple-600" /></div>
                  <audio src={resolvedUrl} controls className="flex-1" />
                </div>
              ) : mediaType === 'document' ? (
                <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-gray-50 dark:bg-gray-900 p-4 rounded-xl hover:bg-gray-100 transition-colors" data-testid="post-media-document">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><FileText className="h-6 w-6 text-blue-600" /></div>
                  <div className="flex-1"><p className="font-medium text-gray-900 dark:text-white">Document</p><p className="text-sm text-gray-500">Cliquez pour ouvrir</p></div>
                  <ExternalLink className="h-5 w-5 text-gray-400" />
                </a>
              ) : (
                <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-purple-300 transition-colors" data-testid="post-media-link">
                  {post.og_data?.image ? (
                    <>
                      <img src={post.og_data.image} alt="" className="w-full h-48 object-cover" />
                      <div className="p-4 bg-white dark:bg-gray-800">
                        <p className="font-semibold text-gray-900 dark:text-white line-clamp-2">{post.og_data.title || post.media_url}</p>
                        {post.og_data.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{post.og_data.description}</p>}
                        <p className="text-xs text-purple-600 mt-2 flex items-center gap-1"><LinkIcon className="h-3 w-3" />{post.og_data.domain || post.media_url}</p>
                      </div>
                    </>
                  ) : post.og_data?.title ? (
                    <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center gap-4">
                      <div className="w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"><LinkIcon className="h-7 w-7 text-purple-600" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white line-clamp-1">{post.og_data.title}</p>
                        {post.og_data.description && <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{post.og_data.description}</p>}
                        <p className="text-xs text-purple-600 mt-1">{post.og_data.domain || post.media_url}</p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-purple-400 flex-shrink-0" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center shadow-sm"><LinkIcon className="h-6 w-6 text-purple-600" /></div>
                      <div className="flex-1 min-w-0"><p className="font-medium text-gray-900 dark:text-white truncate">{post.media_url}</p><p className="text-sm text-purple-600">Ouvrir le lien</p></div>
                      <ExternalLink className="h-5 w-5 text-purple-400" />
                    </div>
                  )}
                </a>
              )}
            </div>
          );
        })()}

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700 mt-3">
          <ReactionButton postId={pid} reactions={post.reactions || {}} userReaction={post.user_reaction} />
          <button
            onClick={toggleComments}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              commentsOpen ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'text-gray-500 dark:text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30'
            }`}
            data-testid={isWall ? `wall-comment-btn-${pid}` : 'comment-button'}
          >
            <MessageCircle className={`h-5 w-5 ${commentsOpen ? 'fill-purple-100' : ''}`} />
            <span className="text-sm font-medium">{post.comment_count || 0}</span>
          </button>
          <button
            onClick={() => onShare && onShare(post)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition-all"
            data-testid={isWall ? 'wall-share-button' : 'share-button'}
          >
            <Share2 className="h-5 w-5" /><span className="text-sm font-medium hidden sm:inline">Partager</span>
          </button>
        </div>

        {/* Comments — pleine largeur de la card (Brief LOT2.3) */}
        {commentsOpen && (
          <div className="mt-3">
            <CommentsSection postId={pid} initialCount={post.comment_count || 0} />
          </div>
        )}
      </CardContent>

      {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </Card>
  );
};

export default PostCard;
